import { FC, useEffect } from 'react';
import { Tabs, Typography } from 'antd';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { getUserProfile } from 'features/user/actions';
import { getBuyersCounteroffers } from 'features/secondaryMarket/actions';

import { AgreeWithTerms } from 'componets/ui';

import { Paths } from 'utils/constants';

import { selectUser } from 'selectors';

const TABS = {
	OFFERS: 'offers',
	RECEIVED: 'receivedCounteoffers',
	SENT: 'sentCounteroffers',
};

const TAB_PATHS: Record<string, string> = {
	[TABS.OFFERS]: Paths.SECONDARY_MARKET,
	[TABS.RECEIVED]: Paths.COUNTEROFFERS_RECEIVED,
	[TABS.SENT]: Paths.COUNTEROFFERS_SENT,
};

export const SecondaryMarket: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const user = useAppSelector(selectUser);
	const buyerCounteroffersCount = useAppSelector(
		(state) => state.secondaryMarket.buyerCounterofferPager.totalCount,
	);

	const counteroffersCount = user?.secondaryMarket?.counterOffers?.count;

	const t = useTranslate([
		'secondaryMarketText',
		'receivedCounteroffersText',
		'sentCounteroffersText',
	]);

	useEffect(() => {
		dispatch(getUserProfile());
		dispatch(getBuyersCounteroffers());
	}, [dispatch]);

	const isReceived = pathname.startsWith(Paths.COUNTEROFFERS_RECEIVED);
	const isSent = pathname.startsWith(Paths.COUNTEROFFERS_SENT);

	const type = isReceived ? TABS.RECEIVED : isSent ? TABS.SENT : TABS.OFFERS;

	const isTabs = counteroffersCount > 0 || buyerCounteroffersCount > 0;

	const isCountsLoading = counteroffersCount === undefined || buyerCounteroffersCount === undefined;

	if (!isCountsLoading) {
		if (isReceived && !counteroffersCount) {
			return <Navigate to={Paths.SECONDARY_MARKET} replace />;
		}

		if (isSent && !buyerCounteroffersCount) {
			return <Navigate to={Paths.SECONDARY_MARKET} replace />;
		}
	}

	return (
		<>
			<div>
				{isTabs ? (
					<Tabs
						className="accountTabs"
						type="card"
						defaultActiveKey={type}
						activeKey={type}
						onChange={(v: string) => navigate(TAB_PATHS[v])}
						destroyInactiveTabPane
					>
						<Tabs.TabPane tab={t.secondaryMarketText} key={TABS.OFFERS}>
							{type === TABS.OFFERS && <Outlet />}
						</Tabs.TabPane>

						{counteroffersCount > 0 && (
							<Tabs.TabPane
								tab={
									<span>
										{t.receivedCounteroffersText}
										<Typography.Text type="danger">&nbsp;({counteroffersCount})</Typography.Text>
									</span>
								}
								key={TABS.RECEIVED}
							>
								{type === TABS.RECEIVED && <Outlet />}
							</Tabs.TabPane>
						)}

						{buyerCounteroffersCount > 0 && (
							<Tabs.TabPane tab={t.sentCounteroffersText} key={TABS.SENT}>
								{type === TABS.SENT && <Outlet />}
							</Tabs.TabPane>
						)}
					</Tabs>
				) : (
					<Outlet />
				)}
			</div>

			{user?.accountId && !user.agreeWithTerms && <AgreeWithTerms />}
		</>
	);
};